import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { get, isEmpty } from "lodash";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import AuthFormWrapper from "../../components/NewLogin/AuthFormWrapper";
import AuthShell from "../../components/NewLogin/AuthShell";
import images from "../../constant/images";
import { redirectToPath } from "../../helper/com-helper";
import { getPath } from "../../helper/commonHelper";
import { getResetPasswordParams } from "../../helper/resetPasswordHelper";
import useRecaptchaV3 from "../../hooks/recaptchaV3";
import { recaptchaSiteKey } from "../../utils/ApiHandler";
import { useLoginHandlers } from "../Login/useLoginHandlers";
import { toast, FOCUS_RING_SHADOW } from "@3sc/common-component";
import { readAppValue } from "../../config/runtimeAccessor";

const FONT = "'Euclid Circular B', sans-serif";
const FONT_REGULAR = "'Euclid Circular Light', sans-serif";

const labelSx = {
  fontFamily: FONT_REGULAR,
  fontSize: "1.143rem",
  fontWeight: 300,
  lineHeight: "1.429rem",
  color: "#474849",
  display: "block",
  mb: "0.271rem",
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    height: "3.429rem",
    borderRadius: "0.286rem",
    fontFamily: FONT,
    fontSize: "1.048rem",
    fontWeight: 300,
    lineHeight: "1.1233rem",
    color: "#525252",
    "& fieldset": { borderColor: "#D0D4DA" },
    "&:hover fieldset": { borderColor: "#07080B" },
    "&.Mui-focused fieldset": { borderColor: "#07080B", borderWidth: "0.095rem" },
    "&.Mui-focused": { boxShadow: FOCUS_RING_SHADOW },
  },
  "& input::placeholder": { color: "#525252", opacity: 1 },
  "& .MuiOutlinedInput-input": {
    WebkitBoxShadow: "0 0 0 30px white inset !important",
  },
};

const PASSWORD_RULES = [
  {
    key: "length",
    label: "Be at least 10 characters long",
    test: (v) => v.length >= 10,
  },
  {
    key: "uppercase",
    label: "Include at least one uppercase letter (A–Z)",
    test: (v) => /[A-Z]/.test(v),
  },
  {
    key: "number",
    label: "Include at least one number (0–9)",
    test: (v) => /\d/.test(v),
  },
  {
    key: "special",
    label: "Include at least one special character (e.g. ! @ # $ % ^ & *)",
    test: (v) => /[^A-Za-z0-9]/.test(v),
  },
];

const RULE_COLORS = {
  pass: "#12B76A",
  fail: "#E5484D",
};

const RULE_ICONS = {
  pass: CheckRoundedIcon,
  fail: CloseRoundedIcon,
  neutral: FiberManualRecordIcon,
};

// Empty password → neutral; otherwise pass/fail from the rule's own result.
const getRuleState = (password, rule) => {
  if (password) return rule.passed ? "pass" : "fail";
  return "neutral";
};

function ResetPassword() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = readAppValue("user");
  const { t } = useTranslation();
  const executeRecaptcha = useRecaptchaV3(recaptchaSiteKey);
  const location = useLocation();
  const [isMac, setIsMac] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { resetPasswordSubmit } = useLoginHandlers({
    dispatch,
    navigate,
    t,
    executeRecaptcha,
  });

  const { uid, token } = getResetPasswordParams(location);

  useEffect(() => {
    if ((!uid || !token) && location.pathname?.includes("reset-password")) {
      toast.error(t("invalidResetLink"));
      navigate(`/`);
    }
  }, [uid, token, location.pathname, navigate, t]);

  useEffect(() => {
    if (!isEmpty(user)) {
      const permissions = get(user, "permissions", []);
      const roles = get(user, "roles", []);
      const path = getPath(permissions, roles);
      redirectToPath(path);
    }
  }, [user]);

  useEffect(() => {
    const platform = navigator.userAgentData?.platform || navigator.userAgent || "";
    setIsMac(platform.toLowerCase().includes("mac"));
  }, []);

  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
    showNewPassword: false,
    showConfirmPassword: false,
  });

  const { confirmPassword, password, showNewPassword, showConfirmPassword } = formData;

  const ruleStates = PASSWORD_RULES.map((rule) => ({
    ...rule,
    passed: rule.test(password),
  }));
  const allRulesPassed = ruleStates.every((rule) => rule.passed);

  const isDisabled =
    !password ||
    !confirmPassword ||
    !allRulesPassed ||
    isSubmitting;

  const handleClickShowPassword = (key) => {
    const formKey = key === "confirmPassword" ? "showConfirmPassword" : "showNewPassword";

    setFormData((current) => ({
      ...current,
      [formKey]: !current[formKey],
    }));
  };

  const handleChange = (prop) => (event) => {
    const value = event.target.value;

    setFormData((current) => ({
      ...current,
      [prop]: value,
    }));
  };

  const handleResetPassword = async () => {
    if (isDisabled) return;

    setIsSubmitting(true);
    await resetPasswordSubmit({
      payload: {
        password,
        confirm_password: confirmPassword,
        uid,
        token,
      },
    });
    setIsSubmitting(false);
  };

  return (
    <AuthShell isLoading={false} isMac={isMac}>
      <AuthFormWrapper>
        <Box
          sx={{
            height: "100%",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Stack alignItems="center" spacing="0.476rem" sx={{ mb: "2.7rem" }} gap="1.429rem">
            <Box component="img" src={images.SCAI} alt="SCAI" />

            <Stack alignItems="center" spacing="0.276rem" width="100%">
              <Typography
                variant="h1"
                sx={{
                  fontFamily: FONT,
                  fontSize: "1.8rem",
                  fontWeight: 400,
                  lineHeight: "1.857rem",
                  color: "#07080B",
                  textAlign: "center",
                }}
              >
                Set New Password
              </Typography>
              <Typography
                sx={{
                  fontFamily: FONT_REGULAR,
                  fontSize: "1.143rem",
                  fontWeight: 300,
                  lineHeight: "1.714rem",
                  color: "#474849",
                  textAlign: "center",
                  whiteSpace: "nowrap",
                }}
              >
                Create a new password to secure your account.
              </Typography>
            </Stack>
          </Stack>

          <Stack spacing="0.952rem" sx={{ width: "100%", mb: "1.7rem" }}>
            <Box>
              <Typography component="label" htmlFor="reset-password" sx={labelSx}>
                New Password
              </Typography>
              <TextField
                id="reset-password"
                fullWidth
                size="small"
                type={showNewPassword ? "text" : "password"}
                placeholder="Type here..."
                name="password"
                data-testid="password-input"
                value={password}
                onChange={handleChange("password")}
                autoComplete="new-password"
                sx={inputSx}
                slotProps={{
                  input: {
                    "aria-label": "New password",
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showNewPassword ? "Hide password" : "Show password"}
                          edge="end"
                          size="small"
                          onClick={() => handleClickShowPassword("password")}
                          sx={{ color: "#666D80", mr: "-0.381rem" }}
                        >
                          <Box
                            component="img"
                            src={showNewPassword ? images.LockOpenPasswordIcon : images.LockedpasswordIcon}
                            alt="visibility"
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Box>

            <Box>
              <Typography component="label" htmlFor="reset-confirm-password" sx={labelSx}>
                Confirm Password
              </Typography>
              <TextField
                id="reset-confirm-password"
                fullWidth
                size="small"
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Type here..."
                name="confirmPassword"
                data-testid="confirm-password-input"
                value={confirmPassword}
                onChange={handleChange("confirmPassword")}
                autoComplete="new-password"
                sx={inputSx}
                slotProps={{
                  input: {
                    "aria-label": "Confirm password",
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                          edge="end"
                          size="small"
                          onClick={() => handleClickShowPassword("confirmPassword")}
                          sx={{ color: "#666D80", mr: "-0.381rem" }}
                        >
                          <Box
                            component="img"
                            src={showConfirmPassword ? images.LockOpenPasswordIcon : images.LockedpasswordIcon}
                            alt="visibility"
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Box>
          </Stack>

          <Stack spacing="0.286rem" sx={{ p: "1rem 0 2rem 0" }}>
            {ruleStates.map((rule) => {
              const state = getRuleState(password, rule);
              const color = RULE_COLORS[state];
              const RuleIcon = RULE_ICONS[state] ?? FiberManualRecordIcon;

              return (
                <Stack
                  key={rule.key}
                  direction="row"
                  alignItems="center"
                  spacing="0.476rem"
                >
                  <RuleIcon
                    sx={{ fontSize: "0.8rem", color:(theme) => RULE_COLORS[state] ? color :  theme.palette.primary.textColor }}
                  />
                  <Typography
                    sx={{
                      color:(theme) => RULE_COLORS[state] ? color :  theme.palette.primary.textColor, 
                      fontSize: "1rem",
                      fontWeight: 300,
                      lineHeight: "1.286rem",
                    }}
                  >
                    {rule.label}
                  </Typography>
                </Stack>
              );
            })}
          </Stack>

          <Box
            sx={{
              width: "100%",
              border: "0.095rem solid #07080B",
              borderRadius: "0.190rem",
              padding: "1px",
              boxSizing: "border-box",
              "&:hover": { border: "0.095rem solid rgb(233, 79, 28) !important" },
            }}
          >
            <Button
              fullWidth
              onClick={handleResetPassword}
              disabled={isDisabled}
              data-testid="login-button"
              aria-label="Update password"
              endIcon={<KeyOutlinedIcon sx={{ fontSize: "1.1rem" }} />}
              sx={{
                fontFamily: FONT_REGULAR,
                fontSize: "1.043rem",
                fontWeight: 300,
                lineHeight: "1.524rem",
                color: "#FFFFFF",
                backgroundColor: "#07080B",
                borderRadius: "0.190rem",
                height: "3rem",
                padding: "0.952rem 2.286rem",
                textTransform: "none",
                "&:hover": { backgroundColor: "rgb(233, 79, 28)", color: "#ffffff" },
                "&.Mui-disabled": { backgroundColor: "#07080B", color: "#FFFFFF", opacity: 0.6 },
              }}
            >
              {isSubmitting ? (
                <CircularProgress size="1.524rem" sx={{ color: "#fff" }} />
              ) : (
                "Update Password"
              )}
            </Button>
          </Box>
        </Box>
      </AuthFormWrapper>
    </AuthShell>
  );
}

export default ResetPassword;
